import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Movie } from "@/types/content";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const navigate = useNavigate();
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (dir: number) => {
      setActive((prev) => (prev + dir + items.length) % items.length);
    },
    [items.length],
  );

  useEffect(() => {
    const timer = setInterval(() => go(1), 5500);
    return () => clearInterval(timer);
  }, [go, active]);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1);
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full h-[440px] md:h-[560px] overflow-hidden rounded-none lg:rounded-xl bg-foreground-950"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? "opacity-100 z-10" : "opacity-0 z-0"
          }`}
        >
          <img
            src={item.cover}
            alt={`${item.title} 焦点海报`}
            title={`${item.title} - 蜜月TV热门影视`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/25" />
          <div className="absolute inset-0 flex items-end">
            <div className="w-full px-6 md:px-12 pb-12 md:pb-16 max-w-3xl">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-bold">
                  焦点推荐
                </span>
                {item.tags.slice(0, 2).map((tag) => (
                  <span
                    key={tag}
                    className="px-2.5 py-1 rounded-md bg-background-50/15 text-background-50 text-xs backdrop-blur"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h2 className="text-2xl md:text-4xl font-black text-white mb-3 leading-tight">
                {item.title}
              </h2>
              <p className="text-sm md:text-base text-background-100/90 leading-relaxed mb-5 line-clamp-2 max-w-xl">
                {item.synopsis}
              </p>
              <div className="flex items-center gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => navigate(`/detail/${item.id}`)}
                  className="whitespace-nowrap flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-play-fill" />
                  查看详情
                </button>
                <span className="text-sm text-background-100/80">
                  {item.region} · {item.year} · 评分 {item.rating.toFixed(1)}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      <button
        type="button"
        aria-label="上一张"
        onClick={() => go(-1)}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 text-white backdrop-blur hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        aria-label="下一张"
        onClick={() => go(1)}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 items-center justify-center rounded-full bg-background-50/20 text-white backdrop-blur hover:bg-background-50/35 transition-colors cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 right-6 md:right-12 z-20 flex items-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到第${index + 1}张`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === active ? "w-7 bg-primary-500" : "w-3 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}